/**
 * /developers/quickstart — 60-second developer quickstart for the CARTIE
 * LLM Proxy + Virtual Tenant + Budget Negotiator.
 *
 * v34.4 (Mar 2026): explicitly designed for the "I just want a code example"
 * developer who lands from HN / Twitter / a tweet. Single page, no fluff,
 * all the value-prop framed as code.
 *
 * Marketing-style dark theme. Mounted at `/developers/quickstart`.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import {
  ArrowLeft, ArrowRight, Copy, Check, Zap, ShieldCheck, Sparkles,
  Terminal, GitBranch, Bot, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Code samples ───────────────────────────────────────────────────────
const SAMPLES = {
  python: `import openai

client = openai.OpenAI(
    api_key="sk-...",                        # your OpenAI key
    base_url="https://api.cartieai.com/v1",  # ← one-line change
    default_headers={
        "X-Cartie-Key":     "cartie_proxy_xxxx",       # from app.cartieai.com
        "X-Cartie-Tenant":  "marketing-usa",           # who's spending?
        "X-Cartie-Project": "social-media-bot",        # which project?
        "X-Cartie-Budget":  "500",                     # auto-block at $500/mo
    },
)

# Same OpenAI payload — but 70% cheaper via Smart Router,
# every call logged + attributed + cost-capped.
resp = client.chat.completions.create(
    model="gpt-5.2",
    messages=[{"role": "user", "content": "Summarise this PDF in 3 bullets..."}],
)`,

  node: `import OpenAI from "openai";

const openai = new OpenAI({
  apiKey: process.env.OPENAI_API_KEY,
  baseURL: "https://api.cartieai.com/v1",   // ← one-line change
  defaultHeaders: {
    "X-Cartie-Key":     "cartie_proxy_xxxx",     // from app.cartieai.com
    "X-Cartie-Tenant":  "marketing-usa",         // who's spending?
    "X-Cartie-Project": "social-media-bot",      // which project?
    "X-Cartie-Budget":  "500",                   // auto-block at $500/mo
  },
});

// Same payload. 70% cheaper. Every call logged + attributed.
const resp = await openai.chat.completions.create({
  model: "gpt-5.2",
  messages: [{ role: "user", content: "Summarise this PDF in 3 bullets..." }],
});`,

  curl: `curl https://api.cartieai.com/v1/chat/completions \\
  -H "Authorization: Bearer $OPENAI_API_KEY" \\
  -H "X-Cartie-Key: cartie_proxy_xxxx" \\
  -H "X-Cartie-Tenant: marketing-usa" \\
  -H "X-Cartie-Project: social-media-bot" \\
  -H "X-Cartie-Budget: 500" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-5.2",
    "messages": [{"role": "user", "content": "Summarise this PDF..."}]
  }'`,
};

const LANGS = [
  { id: 'python', label: 'Python', icon: Terminal },
  { id: 'node',   label: 'Node.js', icon: Terminal },
  { id: 'curl',   label: 'cURL', icon: Terminal },
];

const DevelopersQuickstart = () => {
  const [lang, setLang] = useState('python');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SAMPLES[lang]);
      setCopied(true);
      toast.success('Snippet copied');
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="developers-quickstart-page">
      <SEOHead
        title="60-Second Developer Quickstart — CARTIE LLM Proxy"
        description="Drop-in OpenAI-compatible gateway. Change one URL, get 70% cost reduction, per-team attribution, automatic budget cutoffs, and audit trails. Code in Python, Node.js, cURL."
        canonical="https://cartieai.com/developers/quickstart"
        keywords="openai proxy, llm gateway, finops gateway, ai cost tracking, openai middleware, token budget"
      />

      {/* Sticky nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950/80 border-b border-slate-800/60">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white text-sm" data-testid="quickstart-back">
            <ArrowLeft className="w-4 h-4" /> CARTIEAI
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/llm-proxy" className="text-slate-400 hover:text-white" data-testid="quickstart-dashboard-link">
              Open Dashboard
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400"
              data-testid="quickstart-signup"
            >
              Get a key <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-14">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-5">
            <Terminal className="w-3 h-3" /> 60-second quickstart
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]" data-testid="quickstart-h1">
            Change one URL.<br />
            <span className="text-emerald-300 drop-shadow-[0_0_18px_rgba(16,185,129,0.45)]">
              Get a real FinOps system for AI.
            </span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mt-5 leading-relaxed">
            Drop-in replacement for the OpenAI / Anthropic base URL. <span className="text-white font-semibold">Same SDK, same payloads</span> — plus per-team attribution, automatic budget cutoffs, full audit trail, and 70%-cheaper routing.
          </p>
        </div>

        {/* Code panel */}
        <div className="mt-12 rounded-2xl border border-emerald-500/30 overflow-hidden bg-slate-900/60 shadow-xl shadow-emerald-500/5">
          {/* Lang tabs + copy */}
          <div className="flex items-center justify-between border-b border-emerald-500/20 px-3 py-2 bg-slate-950/50">
            <div className="flex gap-1">
              {LANGS.map(l => {
                const Ic = l.icon;
                const active = lang === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setLang(l.id)}
                    data-testid={`quickstart-lang-${l.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono transition-all ${active ? 'bg-emerald-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-emerald-300 hover:bg-emerald-500/10'}`}
                  >
                    <Ic className="w-3.5 h-3.5" /> {l.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-emerald-300 hover:text-white hover:bg-emerald-500/10 transition-colors"
              data-testid="quickstart-copy"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {/* Code body */}
          <pre className="p-5 sm:p-7 text-xs sm:text-sm leading-relaxed text-slate-200 overflow-x-auto font-mono" data-testid="quickstart-code">
            <code>{SAMPLES[lang]}</code>
          </pre>
        </div>

        {/* What you get */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="quickstart-benefits">
          {[
            { Icon: Zap, accent: 'emerald', title: '70% cheaper', body: 'Smart Router auto-picks the cheapest quality-matched provider per intent. Same prompt, fraction of the cost.' },
            { Icon: ShieldCheck, accent: 'amber', title: 'Budget cutoffs', body: 'Set X-Cartie-Budget. We block at the threshold, alert Slack, and route fallback intents to a cheaper tier.' },
            { Icon: Activity, accent: 'rose', title: 'Per-team P&L', body: 'X-Cartie-Tenant + X-Cartie-Project headers attribute every token to a cost center. Stripe-billable.' },
            { Icon: GitBranch, accent: 'teal', title: 'Audit trail', body: 'Immutable per-trace ledger. SOC 2 / ISO 27001-ready CSV export. No more "mystery bill".' },
          ].map((b, i) => {
            const cls = {
              emerald: { ring: 'border-emerald-500/30', icon: 'text-emerald-300', glow: 'bg-emerald-500/15' },
              amber:   { ring: 'border-amber-500/30',   icon: 'text-amber-300',   glow: 'bg-amber-500/15' },
              rose:    { ring: 'border-rose-500/30',    icon: 'text-rose-300',    glow: 'bg-rose-500/15' },
              teal:    { ring: 'border-teal-500/30',    icon: 'text-teal-300',    glow: 'bg-teal-500/15' },
            }[b.accent];
            const Ic = b.Icon;
            return (
              <div key={i} className={`relative rounded-2xl border bg-slate-900/60 p-5 overflow-hidden ${cls.ring}`} data-testid={`quickstart-benefit-${i}`}>
                <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl pointer-events-none ${cls.glow}`} />
                <div className="relative">
                  <Ic className={`w-7 h-7 mb-3 ${cls.icon}`} />
                  <h3 className="text-base font-bold text-white">{b.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed mt-1">{b.body}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* What happens on first request */}
        <div className="mt-16 rounded-2xl border border-white/10 bg-slate-900/50 p-7">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-300/80 mb-3">
            What happens on your first request
          </div>
          <ol className="space-y-3 text-sm text-slate-300">
            {[
              ['Forwards to OpenAI (or Anthropic, Gemini, Mistral, Meta)', 'verbatim payload. Your provider key is never stored.'],
              ['Smart Router checks intent class', '— if a cheaper model would match quality, we re-route.'],
              ['Calculates cost in real time', '— from the latest pricing table per provider.'],
              ['Attributes to your tenant + project', '— per the X-Cartie-* headers.'],
              ['Logs to immutable ledger', '— visible in /admin/llm-proxy live console.'],
              ['Enforces the budget envelope', '— soft warning at 80%, hard block at 100%.'],
              ['Returns the response unchanged', '— your app sees the same OpenAI / Anthropic / Gemini / Groq / Mistral shape.'],
            ].map(([head, tail], i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="font-mono text-[11px] font-bold text-emerald-400 mt-0.5 shrink-0 w-7">{String(i + 1).padStart(2, '0')}</span>
                <span><span className="text-white font-semibold">{head}</span> {tail}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* The cost meter promise */}
        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-emerald-500/30 bg-slate-900 bg-gradient-to-br from-slate-900 via-emerald-950/30 to-slate-900 p-7 overflow-hidden relative">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <Bot className="w-7 h-7 text-emerald-300 mb-3" />
              <h3 className="text-lg font-bold text-white">Live cost console</h3>
              <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">
                Every request streams into the <code className="text-emerald-300 bg-slate-950/60 px-1.5 py-0.5 rounded font-mono text-[12px]">/admin/llm-proxy</code> console — model, tenant, tokens, cost, latency. Like <code className="text-emerald-300">tail -f</code> for your AI bill.
              </p>
              <Link
                to="/llm-proxy"
                className="inline-flex items-center gap-1.5 mt-4 text-emerald-300 hover:text-emerald-200 text-sm font-semibold group"
                data-testid="quickstart-open-console"
              >
                Open the live console <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>

          <div className="rounded-2xl border border-emerald-500/30 bg-slate-900 bg-gradient-to-br from-slate-900 via-amber-950/30 to-slate-900 p-7 overflow-hidden relative">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <ShieldCheck className="w-7 h-7 text-amber-300 mb-3" />
              <h3 className="text-lg font-bold text-white">Budget Negotiator (Sub-System Z)</h3>
              <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">
                Pre-sign budget envelopes with Finance once. After that every PR + every request stays inside the envelope automatically — soft warning at 80%, hard block at 100%.
              </p>
              <Link
                to="/budget-negotiator"
                className="inline-flex items-center gap-1.5 mt-4 text-amber-300 hover:text-amber-200 text-sm font-semibold group"
                data-testid="quickstart-open-budget"
              >
                How the envelope works <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-16 text-center" data-testid="quickstart-bottom-cta">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300 mb-4">
            <Sparkles className="w-3 h-3" /> Free tier · 100k tokens/mo
          </div>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
            Stop paying for the <span className="text-emerald-300">"mystery bill"</span>.
          </h3>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto">
            Get a proxy key in 60 seconds. No credit card. Same SDK. Real FinOps from your first request.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Link
              to="/signup"
              className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-emerald-500 text-slate-950 font-bold hover:bg-emerald-400 shadow-lg shadow-emerald-500/20"
              data-testid="quickstart-cta-get-key"
            >
              Get my proxy key <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/llm-proxy"
              className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 font-semibold"
              data-testid="quickstart-cta-open-dashboard"
            >
              See the demo dashboard
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DevelopersQuickstart;
